import { useMcpTool } from 'webmcp-react'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { ConversationsApi } from 'entities/Conversation'
import { MessagesApi } from 'entities/Message'
import {
  listUnreadConversationsSchema,
  searchConversationsSchema,
  openConversationWithContactSchema,
  openConversationSchema,
  replyToConversationSchema,
} from 'entities/WebMcp/model/schemas'
import { serializeConversationSummary } from 'entities/WebMcp/lib/serializers'
import { buildUnreadResult, EMPTY_UNREAD_SCOPE_GUIDANCE } from 'entities/WebMcp/lib/unreadScope'
import {
  findConversationForContact,
  openDraftConversationForContact,
  openConversationInInbox,
  inboxOutcome,
} from 'entities/WebMcp/lib/conversationNavigation'
import { okResult, errorResult } from 'entities/WebMcp/lib/toolResult'

/**
 * Registers the conversation WebMCP tools. Renders nothing.
 * list_unread_conversations is read-only; reply_to_conversation is a write
 * (gated) that posts into an existing thread and then opens it.
 */
export const ConversationsTools = () => {
  const entities = useEntitiesContext()
  const { inboxesStore } = entities

  useMcpTool({
    name: 'list_unread_conversations',
    description:
      'List unread conversations in the inbox the user is currently viewing, and ONLY that inbox. Returns the scope it looked at, plus conversation id, contact, unread count, and the last message. An empty result means none unread in that one inbox, never none anywhere. Use a conversation id with open_conversation or reply_to_conversation.',
    input: listUnreadConversationsSchema,
    annotations: { readOnlyHint: true },
    handler: async () => {
      try {
        // Mirror what the conversation list sends (ConversationListStore): team_id and
        // team_type are a pair, and grouped_response is required for the response to
        // include the flat `conversations` array.
        const { data } = await ConversationsApi.getConversations({
          team_id: inboxesStore.currentInboxTypeId || 0,
          team_type: inboxesStore.currentInboxType || 3,
          filter: 'unread',
          grouped_response: true,
          limit: 25,
        })
        const result = buildUnreadResult(
          (data.conversations ?? []).map((c) => serializeConversationSummary(c)),
          data.conversations_total_count,
          inboxesStore.currentInbox?.name
        )
        return okResult(
          result,
          result.results.length === 0 ? EMPTY_UNREAD_SCOPE_GUIDANCE : undefined
        )
      } catch (e) {
        return errorResult(e)
      }
    },
  })

  useMcpTool({
    name: 'search_conversations',
    description:
      'Search conversations by keyword across message content and contact names — how to find a specific thread the user describes. Returns matching conversation threads with id, contact, and last message. Read-only; present the results in chat, then use open_conversation with the chosen conversation id.',
    input: searchConversationsSchema,
    annotations: { readOnlyHint: true },
    handler: async ({ term, page, per_page }) => {
      try {
        // Wraps the same keyword search the Conversations search page uses
        // (POST messages/search); `conversations` are the distinct matching threads.
        const { data } = await MessagesApi.getSearchMessages({ term, page, per_page })
        const contactsById = new Map((data.contacts ?? []).map((c) => [c.id, c] as const))
        return okResult({
          total: data.total,
          results: data.conversations.map((c) => serializeConversationSummary(c, contactsById)),
        })
      } catch (e) {
        return errorResult(e)
      }
    },
  })

  useMcpTool({
    name: 'open_conversation_with_contact',
    description:
      "Open a contact's conversation thread so the user can read it and reply in the app. Requires contact_id (from search_contacts) and team_id (an inbox id from list_inboxes). If no thread exists yet, an unsent draft addressed to the contact is opened instead. Navigate-first: this opens the thread, it does not send anything.",
    input: openConversationWithContactSchema,
    annotations: { readOnlyHint: true },
    handler: async ({ contact_id, team_id }) => {
      try {
        // Mirror the user's own click: open the thread if one exists, otherwise
        // open an unsent draft. Nothing is persisted until the user sends, which
        // is what keeps this tool genuinely read-only.
        const conversation = await findConversationForContact(contact_id, Number(team_id))

        if (!conversation) {
          await openDraftConversationForContact(entities, contact_id, Number(team_id))
          return okResult(
            {
              opened: true,
              draft: true,
            },
            'No conversation exists with this contact yet, so an unsent draft was opened. Tell the user nothing has been sent.'
          )
        }

        const outcome = inboxOutcome(conversation, Number(team_id))
        await openConversationInInbox(inboxesStore, conversation.id, outcome.inbox_id)
        const { agent_guidance, ...data } = outcome
        return okResult({ opened: true, conversation_id: conversation.id, ...data }, agent_guidance)
      } catch (e) {
        return errorResult(e)
      }
    },
  })

  useMcpTool({
    name: 'open_conversation',
    description:
      'Open an existing conversation thread by its id — use this for a conversation returned by search_conversations, list_unread_conversations, or filter_conversations_list. Navigate-first: it opens the thread, it does not send anything.',
    input: openConversationSchema,
    annotations: { readOnlyHint: true },
    handler: async ({ conversation_id }) => {
      try {
        // Read the thread first so we can switch to the inbox that owns it.
        const { data: conversation } = await ConversationsApi.getById(conversation_id)
        await openConversationInInbox(inboxesStore, conversation_id, conversation.inbox_id)
        return okResult({ opened: true, conversation_id, inbox_id: conversation.inbox_id })
      } catch (e) {
        return errorResult(e)
      }
    },
  })

  useMcpTool({
    name: 'reply_to_conversation',
    description:
      'Reply to an existing conversation by id (e.g. one from list_unread_conversations). Requires conversation_id and the message text. Sending always asks the user to approve it first. Sending the same text to several conversations in one reply is a single approval that lists every recipient, so batch them rather than replying one turn at a time.',
    input: replyToConversationSchema,
    handler: async ({ conversation_id, message }) => {
      try {
        await MessagesApi.createMessage(conversation_id, { message, media_url: [] })
        const { data: conversation } = await ConversationsApi.getById(conversation_id)
        await openConversationInInbox(inboxesStore, conversation_id, conversation.inbox_id)
        return okResult({ replied: true, conversation_id, inbox_id: conversation.inbox_id })
      } catch (e) {
        return errorResult(e)
      }
    },
  })

  return null
}
